/**
 * One provenance / data-state vocabulary, reused on every surface (CD review H3).
 * Icon + WORD, never colour alone (colour-blind-safe). Colours come from the
 * --bm-prov-* tokens. This makes the product's data honesty visible at a glance
 * instead of only inside a marker popup.
 */

import type { Signal } from '@biomax/plugin-surveillance-feed';

export type ProvKind = 'confirmed' | 'probable' | 'unverified' | 'conflict' | 'stale' | 'synthetic';

export const PROV_META: Record<ProvKind, { word: string; icon: string; color: string; bg: string }> = {
  confirmed:  { word: 'Confirmed',  icon: '✓', color: 'var(--bm-prov-confirmed)',  bg: 'var(--bm-prov-confirmed-bg)' },
  probable:   { word: 'Probable',   icon: '≈', color: 'var(--bm-prov-probable)',   bg: 'var(--bm-prov-probable-bg)' },
  unverified: { word: 'Unverified', icon: '?', color: 'var(--bm-prov-unverified)', bg: 'var(--bm-prov-unverified-bg)' },
  conflict:   { word: 'Conflict',   icon: '⚠', color: 'var(--bm-prov-conflict)',   bg: 'var(--bm-prov-conflict-bg)' },
  stale:      { word: 'Stale',      icon: '⏳', color: 'var(--bm-prov-stale)',      bg: 'var(--bm-prov-stale-bg)' },
  synthetic:  { word: 'Synthetic',  icon: '◇', color: 'var(--bm-prov-synthetic)',  bg: 'var(--bm-prov-synthetic-bg)' },
};

/** Resolve a signal's data-state: stale + source-conflict take precedence over
 *  the raw verification status, mirroring the map's marker rules. */
export function provKind(s: Pick<Signal, 'verificationStatus' | 'context'>, stale = false): ProvKind {
  if (stale) return 'stale';
  if (s.context?.conflictingReports?.length) return 'conflict';
  const v = s.verificationStatus as string;
  return v === 'confirmed' || v === 'probable' || v === 'unverified' ? (v as ProvKind) : 'unverified';
}

export function ProvChip({ kind, title, compact = false }: { kind: ProvKind; title?: string; compact?: boolean }) {
  const m = PROV_META[kind];
  return (
    <span
      title={title ?? m.word}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        font: '600 11px/1.4 var(--bm-font-mono, ui-monospace, monospace)',
        color: m.color, background: m.bg, border: `1px solid ${m.color}`,
        borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden>{m.icon}</span>{compact ? '' : ` ${m.word}`}
    </span>
  );
}
